import { EXPERIMENT_IDEAS, type Pitch, type Season, type Script, type Qa } from './index';
export function mockPitch(idea: string, genre = 'Mystery', instruction = ''): Pitch {
  const seed = EXPERIMENT_IDEAS.find((c) => c.idea === idea);
  return {
    title: seed?.title ?? 'A Story Between Us',
    synopsis: idea,
    genre,
    tone: 'Intimate, cinematic, suspenseful',
    conflict: 'A buried truth threatens the one relationship worth saving.',
    dramaticEngine: 'Every answer reveals a more personal question.',
    ending: 'They choose to tell the truth, even when it changes everything.',
    characters: [
      {
        id: 'maya',
        name: genre === 'Romance' ? 'Elena' : 'Maya',
        age: 29,
        role: 'Protagonist',
        personality: 'Observant, guarded, quietly brave',
        motivation: 'Understand the truth without losing her family',
        backstory: 'Returned home after a decade away',
        arc: 'From avoidance to courageous honesty',
        appearance:
          'Fictional adult woman, dark wavy shoulder-length hair, warm brown eyes, distinctive arched eyebrows',
        voice: 'Warm low register, measured English delivery',
      },
      {
        id: 'adam',
        name: genre === 'Romance' ? 'Luca' : 'Adam',
        age: 34,
        role: 'The person keeping the secret',
        personality: 'Charming, conflicted, protective',
        motivation: 'Protect someone he loves',
        backstory: 'Stayed behind to hold the family together',
        arc: 'From control to vulnerability',
        appearance: 'Fictional adult man, short dark hair, deep-set eyes, light stubble',
        voice: 'Soft baritone, deliberate English delivery',
      },
    ],
    ...(instruction ? { tone: `Revised direction: ${instruction}` } : {}),
  };
}
export function mockSeason(pitch: Pitch): Season {
  return {
    storyBible: {
      premise: pitch.synopsis,
      world: 'A contemporary coastal city',
      timeline: 'One week in early autumn',
      themes: ['Belonging', 'Truth', 'Second chances'],
      rules: ['No supernatural explanations', 'Every discovery carries an emotional consequence'],
      seasonArc:
        'An uneasy reunion becomes a search for the truth, ending in a difficult reconciliation.',
      ending: pitch.ending,
    },
    visualBible: {
      style: 'Cinematic realism',
      lighting: 'Practical warm lights against blue-hour exteriors',
      cameraLanguage:
        'Comfortable medium shots and shared two-shots; close-ups for earned reactions',
      palette: ['Deep forest', 'Amber', 'Warm ivory'],
      locations: ['Hotel lobby', 'Fourth-floor corridor', 'Room 407'],
      grade: 'warm',
      score: {
        instruments: 'Felt piano, low strings, a brushed snare',
        motif: 'Three falling piano notes that never resolve',
        tempo: 'Slow, around 70 BPM, tightening near the end',
      },
    },
    episodes: Array.from({ length: 8 }, (_, i) => ({
      number: i + 1,
      title:
        i === 0 ? 'The door left open' : i === 1 ? 'A familiar voice' : `The truth, part ${i + 1}`,
      synopsis:
        i === 0
          ? 'An unexpected message draws Maya back to a place she promised to forget.'
          : `A new clue changes what the characters believe about the secret in episode ${i + 1}.`,
      cliffhanger:
        i === 7
          ? 'At last, the truth is theirs to tell.'
          : 'Someone on the other side knows her name.',
    })),
    canon: [
      {
        subject: 'Maya',
        predicate: 'has not met',
        value: 'Adam before episode 1',
        validFrom: 1,
        knownFrom: 1,
      },
      {
        subject: 'Room 407',
        predicate: 'has been closed since',
        value: '2006',
        validFrom: 1,
        knownFrom: 1,
      },
    ],
  };
}
export function mockScript(): Script {
  const scenes = [
    {
      key: 'lobby',
      title: 'The arrival',
      location: 'Hotel lobby',
      summary: 'Maya receives an unexpected message.',
    },
    {
      key: 'corridor',
      title: 'The approach',
      location: 'Fourth-floor corridor',
      summary: 'She follows the voice toward a sealed room.',
    },
    {
      key: 'room',
      title: 'The reveal',
      location: 'Room 407',
      summary: 'A photograph changes everything.',
    },
  ];
  return {
    screenplay:
      'INT. HOTEL LOBBY — BLUE HOUR\n\nMaya stands beneath a flickering lamp. The telephone rings.\n\nMAYA: I thought nobody lived here anymore.\n\nADAM: Some things never leave.\n\nINT. CORRIDOR — CONTINUOUS\n\nA key waits beneath the door. Maya kneels.\n\nMAYA: Who are you?\n\nINT. ROOM 407 — MOMENTS LATER\n\nAn old photograph lies face down. Maya turns it over.\n\nADAM: You already know.\n\nCUT TO BLACK.',
    scenes: scenes.map((scene) => ({
      ...scene,
      conditions: {
        timeOfDay: 'blue hour, just after sunset',
        weather: 'light rain, low grey cloud',
        light: 'the brass lamp is on; fading blue light through the window',
      },
      outfits: [
        { characterId: 'maya', outfit: 'a red coat over a cream blouse' },
        { characterId: 'adam', outfit: 'a dark charcoal jacket' },
      ],
      space: {
        layout: `${scene.location}: rectangular space, entrance south and window north.`,
        environment:
          'Cream plaster walls with dark oak skirting, worn oak floorboards, a pressed-tin ceiling, a brass lamp on the west wall',
        lighting: 'A brass lamp on the west wall; a tall window to the north.',
        anchors: [
          { id: 'lamp', description: 'Brass lamp against cream wall', position: 'West wall' },
        ],
        cameras: [
          {
            key: 'shared',
            position: 'South side, two metres from actors',
            angle: 'Eye level facing north',
            framing: 'Comfortable medium with room around actors',
            lens: 'Natural normal perspective',
          },
        ],
      },
    })),
    hook: 'A telephone rings in a lobby nobody has used in years.',
    cliffhanger: 'Maya recognises the face in the photograph.',
    posterShotKey: 'shot-9',
    shots: Array.from({ length: 10 }, (_, i) => ({
      key: `shot-${i + 1}`,
      sceneKey: scenes[Math.min(2, Math.floor(i / 4))].key,
      order: i,
      duration: 7,
      cameraKey: 'shared',
      cameraMove: {
        kind: 'hold',
        path: 'Remain at the shared view.',
        focus: 'The actor and their action.',
        pace: 'Steady',
        motivation: 'Let the action and reaction develop together.',
      },
      cutReason: 'The new view follows a motivated action or reaction.',
      beat: i === 0 ? 'hook' : i === 5 ? 'turn' : i === 9 ? 'cliffhanger' : 'reaction',
      blocking: [
        {
          characterId: i % 3 === 2 ? 'adam' : 'maya',
          start: 'At the scene entrance',
          end: 'Leans towards the sound',
          facing: 'Towards the source of the sound',
          gaze: 'Follows the other actor when present',
          response: 'A small motivated breath and change of expression',
        },
      ],
      purpose:
        i === 9
          ? 'End on a compelling cliffhanger'
          : 'Reveal character through small physical choices',
      characterIds: i % 3 === 2 ? ['adam'] : ['maya'],
      location: scenes[Math.min(2, Math.floor(i / 4))].location,
      wardrobe: 'Maya: red coat, cream blouse. Adam: dark charcoal jacket.',
      physicalState: 'Maya holds a brass key in her left hand.',
      emotion: i < 6 ? 'Uncertain, searching' : 'Quietly terrified',
      action: [
        'Maya steps into the empty lobby.',
        'A telephone rings beside a faded register.',
        'Adam watches from the stairs.',
        'Maya answers, her hand trembling.',
        'A brass key slides under the door.',
        'Maya walks down the narrow corridor.',
        'She stops in front of room 407.',
        'The door opens into warm darkness.',
        'She turns over a photograph.',
        'Recognition crosses her face. Cut to black.',
      ][i],
      dialogue:
        i === 3
          ? [
              {
                characterId: 'maya',
                text: 'I thought nobody lived here anymore.',
                delivery: 'half to herself, barely above a whisper, eyes on the dark desk',
              },
            ]
          : i === 5
            ? [
                {
                  characterId: 'adam',
                  text: 'Some things never leave.',
                  delivery: 'low and unhurried, watching her from the stairs',
                },
              ]
            : [],
      ...(i === 3 || i === 5
        ? { afterLines: 'A held silence; the fluorescent tube flickers once.' }
        : {}),
      framing: ['Wide establishing', 'Close-up', 'Medium portrait'][i % 3],
      movement: 'Hold steady for the interaction.',
      lighting: 'Warm practical light, soft blue ambient shadows',
      continuity: [
        'Keep locked cast identity',
        'Red coat remains unchanged',
        'Key stays in left hand',
      ],
      screenPositions: [
        { characterId: i % 3 === 2 ? 'adam' : 'maya', position: i % 3 === 2 ? 'right' : 'left' },
      ] as { characterId: string; position: 'left' | 'center' | 'right' }[],
      sound: 'A fluorescent hum and distant rain against tall windows',
    })),
  };
}
export const mockQa: Qa = {
  safe: true,
  passed: true,
  summary: 'Simulated check passed. Human review is still required for real media.',
  issues: [
    {
      severity: 'info',
      category: 'technical',
      description: 'This result was produced by the simulated provider.',
    },
  ],
};

/** A synthetic 63-second episode used to exercise stateful extended takes. */
export function mockLongScript(): Script {
  const script = mockScript();
  const shots = script.shots.slice(0, 3).map((shot, order) => ({
    ...shot,
    dialogue: [{ characterId: shot.characterIds[0], text: 'I thought you would come back.' }],
    duration: 21,
    order,
    takeBeats: [
      {
        duration: 7,
        action: 'Begin the shared interaction.',
        camera: 'Hold the shared view.',
        dialogue: [{ characterId: shot.characterIds[0], text: 'I thought you would come back.' }],
      },
      {
        duration: 7,
        action: 'Let the listener respond through posture and gaze.',
        camera: 'Continue the same view without resetting.',
        dialogue: [],
      },
      {
        duration: 7,
        action: 'Allow the reaction to finish.',
        camera: 'Keep the ending view readable.',
        dialogue: [],
      },
    ],
  }));
  return { ...script, shots, posterShotKey: shots[0].key };
}
